import { LitElement, css, html, nothing } from '@lit'
import styles from '@nova/styles'
import { formatMoney } from '@nova/utils'
import {
  fullSearchUrlForQuery,
  normalizeStorefrontSearchQuery,
} from '@nova/search-sku'

function storefrontRootPath() {
  const root = window.Shopify?.routes?.root
  if (typeof root === 'string' && root.length) return root.endsWith('/') ? root : `${root}/`
  return '/'
}

/** Same options as main-search + header forms so quick results match full search. */
function fullSearchUrl(searchTerm) {
  return fullSearchUrlForQuery(
    normalizeStorefrontSearchQuery(searchTerm) || String(searchTerm || '').trim(),
  )
}

function pickSuggestImageUrl(product) {
  if (!product) return ''
  const fi = product.featured_image
  if (typeof fi === 'string' && fi) return fi
  if (fi && typeof fi.url === 'string' && fi.url) return fi.url
  if (typeof product.image === 'string' && product.image) return product.image
  const v0 = Array.isArray(product.variants) ? product.variants[0] : null
  if (v0?.featured_image?.url) return String(v0.featured_image.url)
  if (typeof v0?.image === 'string' && v0.image) return v0.image
  return ''
}

function normalizeSuggestProduct(product) {
  if (!product?.id) return null
  const imageUrl = pickSuggestImageUrl(product)
  return {
    ...product,
    featured_image: {
      url: imageUrl,
      alt:
        (typeof product.featured_image === 'object' && product.featured_image?.alt) ||
        product.title ||
        '',
    },
    price_min: product.price_min ?? product.price ?? '',
    compare_at_price_min:
      product.compare_at_price_min ?? product.compare_at_price_max ?? '0',
    vendor: product.vendor ?? '',
  }
}

function suggestImageSrc(url, width) {
  if (!url) return ''
  let u = String(url).trim()
  if (u.startsWith('//')) u = `https:${u}`
  if (/[?&]width=\d+/i.test(u)) return u
  if (/cdn\.shopify\.com|shopifycdn\.net|\/cdn\/shop\//i.test(u)) {
    const sep = u.includes('?') ? '&' : '?'
    return `${u}${sep}width=${width}`
  }
  return u
}

function parseMoneyToCents(amount) {
  if (amount === null || amount === undefined || amount === '') return null
  const s = String(amount).trim().replace(/,/g, '')
  if (!s) return null
  if (/[.]/.test(s)) {
    const n = Number.parseFloat(s)
    return Number.isNaN(n) ? null : Math.round(n * 100)
  }
  const n = Number.parseInt(s, 10)
  return Number.isNaN(n) ? null : n
}

function formatSuggestPrice(amount) {
  const cents = parseMoneyToCents(amount)
  if (cents === null) return ''
  try {
    return formatMoney(cents)
  } catch {
    return `$${(cents / 100).toFixed(2)}`
  }
}

function productIsOnSale(product) {
  const price = parseMoneyToCents(product.price_min ?? product.price)
  const compare = parseMoneyToCents(
    product.compare_at_price_min ?? product.compare_at_price_max,
  )
  if (price === null || compare === null) return false
  return compare > price && compare > 0
}

/** Matches `header-search__predictive-row` aside layout breakpoint */
const PREDICTIVE_ASIDE_MQL = '(min-width: 750px)'

function clampPredictiveAsideCount(value, fallback) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 1) return fallback
  return Math.min(20, Math.floor(n))
}

function getPredictiveAsideLimits() {
  const s = window.predictiveSearchSettings || {}
  return {
    popularDesktop: clampPredictiveAsideCount(s.popularLimitDesktop, 5),
    popularMobile: clampPredictiveAsideCount(s.popularLimitMobile, 5),
    collectionsDesktop: clampPredictiveAsideCount(s.collectionsLimitDesktop, 5),
    collectionsMobile: clampPredictiveAsideCount(s.collectionsLimitMobile, 5),
  }
}

function auraProductCard(product, index = 0) {
  const url =
    product.url || `${storefrontRootPath()}products/${product.handle || ''}`
  const imgUrl = product.featured_image?.url
  const src = imgUrl ? suggestImageSrc(imgUrl, 360) : ''
  const onSale = productIsOnSale(product)
  const priceStr = formatSuggestPrice(product.price_min ?? product.price)
  const compareStr = formatSuggestPrice(
    product.compare_at_price_min ?? product.compare_at_price_max,
  )

  return html`
    <a
      class="header-search__aura-card"
      style=${`--aura-card-i: ${index}`}
      href=${url}
    >
      <div class="header-search__aura-card-media">
        ${onSale
          ? html`<span class="header-search__aura-badge">Sale</span>`
          : nothing}
        ${src
          ? html`<img
              class="header-search__aura-card-img"
              src=${src}
              alt=${product.featured_image?.alt || product.title || ''}
              width="360"
              height="360"
              loading="lazy"
            />`
          : html`<div class="header-search__aura-card-placeholder" aria-hidden="true"></div>`}
      </div>
      <h4 class="header-search__aura-card-title">${product.title}</h4>
      ${onSale && compareStr
        ? html`
            <div class="header-search__aura-card-prices">
              <p class="header-search__aura-card-price">${priceStr}</p>
              <p class="header-search__aura-card-compare">${compareStr}</p>
            </div>
          `
        : html`<p class="header-search__aura-card-price">${priceStr}</p>`}
    </a>
  `
}

class NovaSearchResult extends LitElement {
  static properties = {
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
    _asideIsWide: { type: Boolean, state: true },
    _queriesExpanded: { type: Boolean, state: true },
    _collectionsExpanded: { type: Boolean, state: true },
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null
    this._asideIsWide = false
    this._queriesExpanded = false
    this._collectionsExpanded = false

    this._asideMql = window.matchMedia(PREDICTIVE_ASIDE_MQL)
    this._onAsideMql = (e) => {
      this._asideIsWide = e.matches
    }
    this._asideIsWide = this._asideMql.matches
    if (typeof this._asideMql.addEventListener === 'function') {
      this._asideMql.addEventListener('change', this._onAsideMql)
    } else if (typeof this._asideMql.addListener === 'function') {
      // Safari fallback
      this._asideMql.addListener(this._onAsideMql)
    }

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this._asideMql && this._onAsideMql) {
      if (typeof this._asideMql.removeEventListener === 'function') {
        this._asideMql.removeEventListener('change', this._onAsideMql)
      } else if (typeof this._asideMql.removeListener === 'function') {
        this._asideMql.removeListener(this._onAsideMql)
      }
    }

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
  }

  updated(changedProperties) {
    super.updated(changedProperties)

    if (changedProperties.has('searchTerm') || changedProperties.has('searchData')) {
      this._queriesExpanded = false
      this._collectionsExpanded = false
    }

    // More robust check for Safari compatibility
    const drawer = this.closest('.js-header-drawer[data-type="search"]')
    const isDrawerOpen = Boolean(
      drawer &&
        (drawer.hasAttribute('is-open') ||
          drawer.hasAttribute('open') ||
          drawer.open === true),
    )

    // Only return early if we're explicitly closed AND the drawer isn't open
    if (this.openState === 'closed' && !isDrawerOpen) return

    if (Reflect.ownKeys(this.searchData).length === 0) return
    const products =
      this.searchData.resources?.results?.products ?? []
    const productsRenderable = products
      .map(normalizeSuggestProduct)
      .filter(Boolean)
    this.noSearchResult = productsRenderable.length === 0

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    }

    this.handleRenderHeight()
  }

  async handleResult() {
    try {
      const response = await fetch(
        `?section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
      }
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      this.suggestedResult = htmlDocument
      this.handleRenderResult(this.suggestedResult)
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  handleRenderHeight() {
    // More robust check for Safari compatibility
    const drawer = this.closest('.js-header-drawer[data-type="search"]')
    const isDrawerOpen = Boolean(
      drawer &&
        (drawer.hasAttribute('is-open') ||
          drawer.hasAttribute('open') ||
          drawer.open === true),
    )

    // Only return early if we're explicitly closed AND the drawer isn't open
    if (this.openState === 'closed' && !isDrawerOpen) return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      const spaceBelow = window.innerHeight - resultRec.top - 12
      const fits = window.innerHeight - resultRec.bottom > 4
      if (fits || !Number.isFinite(spaceBelow)) {
        this.style.maxHeight = ''
        return
      }
      const cap = Math.max(200, Math.min(spaceBelow, window.innerHeight - 24))
      this.style.maxHeight = cap >= 200 ? `${cap}px` : ''
    }, 120)
  }

  handleRenderResult(data) {
    const suggestedResult = data.querySelector('.suggested-results')
    if (!suggestedResult) return

    const productContainer = suggestedResult.querySelector(
      '.header-search__product-container',
    )
    const currentProductContainer = this.shadowRoot.querySelector(
      '.header-search__product-container',
    )
    if (productContainer && currentProductContainer) {
      currentProductContainer.replaceWith(productContainer.cloneNode(true))
      this.assignProductCardProperties()
    }
  }

  assignProductCardProperties() {
    const productCardContainers = this.shadowRoot.querySelectorAll(
      '.js-suggested-product-card',
    )

    const getValuesFromTemplate = (productId, type) => {
      const template = this.shadowRoot.querySelector(
        `#suggested-product__${type}-json-${productId}`,
      )
      return template ? JSON.parse(template.content.textContent.trim()) : []
    }

    for (const productContainer of productCardContainers) {
      const productId = productContainer.dataset.id
      const swatchValues = getValuesFromTemplate(productId, 'swatch-values')
      const sizeValues = getValuesFromTemplate(productId, 'size-values')
      const variants = getValuesFromTemplate(productId, 'variants')
      const productCard = productContainer.querySelector('nova-product-card')

      productCard.swatches = swatchValues
      productCard.sizes = sizeValues
      productCard.variants = variants
      productCard.mode = 'predictive-search'
    }
  }

  render() {
    if (Reflect.ownKeys(this.searchData).length === 0) return nothing

    const results = this.searchData.resources?.results
    const products = results?.products ?? []
    const productsRenderable = products
      .map(normalizeSuggestProduct)
      .filter(Boolean)

    const resultsSafe = results ?? {}
    const queries = resultsSafe.queries ?? []
    const collections = resultsSafe.collections ?? []
    const pages = [
      ...(resultsSafe.pages ?? []),
      ...(resultsSafe.articles ?? []),
    ]
    const rawLimit = Number(window.predictiveSearchSettings?.productLimit)
    const productCap =
      Number.isFinite(rawLimit) && rawLimit > 0
        ? Math.min(10, Math.floor(rawLimit))
        : 8
    const displayProducts = productsRenderable.slice(0, productCap)
    const searchUrl = fullSearchUrl(this.searchTerm)
    const hasAside =
      queries.length > 0 || collections.length > 0 || pages.length > 0

    // Predictive search can miss matches that full storefront search finds (e.g. "501" vs "501UG").
    if (productsRenderable.length === 0 && !hasAside) {
      return html`
        <div class="header-search__result">
          <div
            class="header-search__no-result-container header-search__mobile-padding"
          >
            <div class="header-search__no-result-content">
              <p class="header-search__no-result-description">
                ${window.searchStrings.predictive_no_quick_matches}
              </p>
              <a class="header-search__view-all header-search__view-all--full" href="${searchUrl}">
                ${window.searchStrings.predictive_view_all}
              </a>
            </div>
          </div>
          <div class="header-search__product-container"></div>
        </div>
      `
    }

    const asideLimits = getPredictiveAsideLimits()
    const popularLimit = this._asideIsWide
      ? asideLimits.popularDesktop
      : asideLimits.popularMobile
    const collectionLimit = this._asideIsWide
      ? asideLimits.collectionsDesktop
      : asideLimits.collectionsMobile
    const queriesOverflow = queries.length > popularLimit
    const visibleQueries =
      this._queriesExpanded || !queriesOverflow
        ? queries
        : queries.slice(0, popularLimit)
    const collectionsOverflow = collections.length > collectionLimit
    const visibleCollections =
      this._collectionsExpanded || !collectionsOverflow
        ? collections
        : collections.slice(0, collectionLimit)

    return html`
      <div class="header-search__result header-search__result--predictive">
        ${displayProducts.length > 0 || hasAside
          ? html`
              <div class="header-search__predictive-scroll">
                <div class="header-search__predictive-inner">
                  <div
                    class="header-search__predictive-row ${hasAside
                      ? 'header-search__predictive-row--with-aside'
                      : ''}"
                  >
                    ${hasAside
                      ? html`
                          <aside class="header-search__predictive-aside">
                            ${queries.length
                              ? html`
                                  <section class="header-search__aside-section">
                                    <h4 class="header-search__aside-heading">
                                      ${window.searchStrings
                                        .predictive_popular_suggestions}
                                    </h4>
                                    <ul class="header-search__aside-list header-search__aside-list--aura">
                                      ${visibleQueries.map(
                                        (q) => html`
                                          <li>
                                            <a
                                              class="header-search__aura-suggest-link"
                                              href="${q.url}"
                                              >${q.text}</a
                                            >
                                          </li>
                                        `,
                                      )}
                                    </ul>
                                    ${queriesOverflow
                                      ? html`
                                          <button
                                            type="button"
                                            class="header-search__aside-toggle"
                                            @click=${(e) => {
                                              e.preventDefault()
                                              this._queriesExpanded =
                                                !this._queriesExpanded
                                            }}
                                            aria-expanded=${this
                                              ._queriesExpanded
                                              ? 'true'
                                              : 'false'}
                                          >
                                            ${this._queriesExpanded
                                              ? window.searchStrings
                                                  .predictive_show_less
                                              : window.searchStrings
                                                  .predictive_show_more}
                                          </button>
                                        `
                                      : nothing}
                                  </section>
                                `
                              : nothing}
                            ${collections.length
                              ? html`
                                  <section class="header-search__aside-section">
                                    <h4 class="header-search__aside-heading">
                                      ${window.searchStrings.predictive_collections}
                                    </h4>
                                    <ul class="header-search__aside-list">
                                      ${visibleCollections.map(
                                        (c) => html`
                                          <li>
                                            <a
                                              class="header-search__aside-link"
                                              href="${c.url}"
                                              >${c.title}</a
                                            >
                                          </li>
                                        `,
                                      )}
                                    </ul>
                                    ${collectionsOverflow
                                      ? html`
                                          <button
                                            type="button"
                                            class="header-search__aside-toggle"
                                            @click=${(e) => {
                                              e.preventDefault()
                                              this._collectionsExpanded =
                                                !this._collectionsExpanded
                                            }}
                                            aria-expanded=${this
                                              ._collectionsExpanded
                                              ? 'true'
                                              : 'false'}
                                          >
                                            ${this._collectionsExpanded
                                              ? window.searchStrings
                                                  .predictive_show_less
                                              : window.searchStrings
                                                  .predictive_show_more}
                                          </button>
                                        `
                                      : nothing}
                                  </section>
                                `
                              : nothing}
                            ${pages.length
                              ? html`
                                  <section class="header-search__aside-section">
                                    <h4 class="header-search__aside-heading">
                                      ${window.searchStrings.pages}
                                    </h4>
                                    <ul class="header-search__aside-list">
                                      ${pages.map(
                                        (p) => html`
                                          <li>
                                            <a
                                              class="header-search__aside-link"
                                              href="${p.url}"
                                            >
                                              <span
                                                class="header-search__aside-icon header-search__aside-icon--page"
                                                aria-hidden="true"
                                                ><svg
                                                  width="14"
                                                  height="14"
                                                  viewBox="0 0 16 16"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <circle
                                                    cx="8"
                                                    cy="8"
                                                    r="7"
                                                    stroke="currentColor"
                                                    stroke-width="1.2"
                                                  />
                                                  <path
                                                    d="M8 11V8M8 5h.01"
                                                    stroke="currentColor"
                                                    stroke-width="1.2"
                                                    stroke-linecap="round"
                                                  />
                                                </svg></span
                                              >
                                              <span>${p.title}</span>
                                            </a>
                                          </li>
                                        `,
                                      )}
                                    </ul>
                                  </section>
                                `
                              : nothing}
                            <a class="header-search__view-all" href="${searchUrl}">
                              ${window.searchStrings.predictive_view_all}
                            </a>
                          </aside>
                        `
                      : nothing}
                    ${displayProducts.length > 0
                      ? html`
                          <section class="header-search__predictive-products">
                            <div class="header-search__product-container">
                              <h3 class="header-search__products-heading">
                                ${window.searchStrings.predictive_products}
                              </h3>
                              <div class="header-search__aura-grid">
                                ${displayProducts.map((product, index) =>
                                  auraProductCard(product, index),
                                )}
                              </div>
                              ${!hasAside
                                ? html`
                                    <a
                                      class="header-search__view-all header-search__view-all--full"
                                      href="${searchUrl}"
                                      >${window.searchStrings.predictive_view_all}</a
                                    >
                                  `
                                : nothing}
                            </div>
                          </section>
                        `
                      : nothing}
                  </div>
                </div>
              </div>
            `
          : nothing}
      </div>
    `
  }

  static styles = [
    styles.variables,
    css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
      max-height: 100%;
    }

    :host .header-search__result {
      display: flex;
      overflow: hidden;
      flex-direction: column;
      max-height: inherit;
      min-height: 0;
      color: var(--nova-brand-ink);
      gap: var(--nova-space-md);
    }

    :host .header-search__result--predictive {
      overflow: hidden;
      gap: 0;
    }

    :host .header-search__predictive-scroll {
      flex: 1;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: var(--nova-space-lg) var(--nova-space-page-margin);
      background: color-mix(in srgb, var(--nova-color-background) 88%, transparent);
    }

    :host .header-search__predictive-inner {
      width: 100%;
      max-width: 1440px;
      margin: 0 auto;
    }

    :host .header-search__predictive-row {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--nova-space-lg);
      width: 100%;
    }

    @media screen and (min-width: 750px) {
      :host .header-search__predictive-row--with-aside {
        flex-direction: row;
        align-items: flex-start;
        gap: clamp(24px, 4vw, 48px);
      }

      :host .header-search__predictive-row--with-aside .header-search__predictive-aside {
        flex: 0 0 min(240px, 26%);
        max-width: 280px;
        padding-right: var(--nova-space-md);
        border-right: 1px solid var(--nova-brand-light);
      }

      :host .header-search__predictive-row--with-aside .header-search__predictive-products {
        flex: 1;
        min-width: 0;
      }
    }

    :host .header-search__predictive-aside {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
    }

    :host .header-search__predictive-products {
      min-width: 0;
      width: 100%;
    }

    :host .header-search__product-container {
      width: 100%;
    }

    :host .header-search__aside-section {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-sm);
    }

  :host .header-search__aside-heading {
      margin: 0;
      font-family: var(--nova-font-sans);
      font-size: 14px;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: 0.13em;
      text-transform: uppercase;
      color: #eb0a1e;
      border-bottom: 1px dotted #c5c5c5;
      padding-bottom: 4px;
  }

    :host .header-search__aside-list {
      display: flex;
      flex-direction: column;
      gap: 2px;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    :host .header-search__aside-list--aura {
      gap: var(--nova-space-xs);
    }

    :host .header-search__aside-toggle {
      margin: 4px 0 0;
      padding: 6px 0;
      border: none;
      background: none;
      font-family: var(--nova-font-sans);
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2;
      color: var(--nova-brand-primary);
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
      text-align: left;
    }

    :host .header-search__aside-toggle:hover {
      color: var(--nova-brand-ink);
    }

    :host .header-search__aura-suggest-link {
      display: inline-block;
      padding: 1px 0;
      font-size: 18px;
      font-weight: 500;
      line-height: 1.1;
      color: #303030;
      text-decoration: none;
      transition: color 0.15s ease;
  }

    :host .header-search__aura-suggest-link:hover {
      color: var(--nova-brand-primary);
    }

    :host .header-search__aside-link {
      display: flex;
      align-items: center;
      gap: var(--nova-space-xs);
      padding: var(--nova-space-2xs) var(--nova-space-xs);
      border-radius: var(--nova-border-radius-s);
      color: var(--nova-brand-ink);
      text-decoration: none;
      font-size: 14px;
      line-height: 1.35;
    }

    :host .header-search__aside-link:hover {
      background: var(--nova-brand-light);
    }

    :host .header-search__aside-icon {
      display: inline-flex;
      color: var(--nova-brand-subdued);
      flex-shrink: 0;
    }

    :host .header-search__aside-icon svg {
      display: block;
    }

    :host .header-search__view-all {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: var(--nova-space-xs);
      padding: var(--nova-space-sm) var(--nova-space-md);
      border-radius: var(--nova-border-radius-l);
      background: var(--nova-brand-primary);
      color: var(--nova-brand-reverse);
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      text-decoration: none;
      text-align: center;
    }

    :host .header-search__view-all:hover {
      filter: brightness(0.92);
    }

    :host .header-search__view-all--full {
      width: 100%;
      margin-top: var(--nova-space-md);
    }

    :host .header-search__products-heading {
      margin: 0 0 var(--nova-space-md);
      font-family: var(--nova-font-sans);
      font-size: 12px;
      font-weight: 600;
      line-height: 1.2;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--nova-color-subdued);
      animation: header-search-aura-in 0.28s ease both;
    }

    @keyframes header-search-aura-in {
      from {
        opacity: 0;
        transform: translateY(6px);
      }

      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    :host .header-search__aura-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--nova-space-sm);
      width: 100%;
    }

    @media screen and (min-width: 990px) {
      :host .header-search__aura-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: var(--nova-space-sm);
      }
    }

    :host .header-search__aura-card {
      display: block;
      min-width: 0;
      color: inherit;
      text-decoration: none;
      cursor: pointer;
      animation: header-search-aura-in 0.34s ease both;
      animation-delay: calc(var(--aura-card-i, 0) * 40ms);
    }

    @media (prefers-reduced-motion: reduce) {
      :host .header-search__products-heading,
      :host .header-search__aura-card {
        animation: none;
      }
    }

    :host .header-search__aura-card:focus-visible {
      outline: 2px solid var(--nova-utility-focus);
      outline-offset: 3px;
      border-radius: var(--nova-border-radius-m);
    }

    :host .header-search__aura-card-media {
      position: relative;
      margin-bottom: var(--nova-space-sm);
      overflow: hidden;
      border-radius: var(--nova-border-radius-m);
      aspect-ratio: 1;
      background: var(--nova-brand-light);
      transition: box-shadow 0.25s ease;
    }

    :host .header-search__aura-card:hover .header-search__aura-card-media {
      box-shadow: 0 8px 30px rgb(0 0 0 / 0.06);
    }

    :host .header-search__aura-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 1;
      padding: 4px 12px;
      border-radius: 9999px;
      background: var(--nova-brand-primary);
      color: var(--nova-brand-reverse);
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      line-height: 1.2;
    }

    :host .header-search__aura-card-img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      transition: transform 0.45s ease;
    }

    :host .header-search__aura-card:hover .header-search__aura-card-img {
      transform: scale(1.05);
    }

    :host .header-search__aura-card-placeholder {
      width: 100%;
      height: 100%;
      min-height: 120px;
      background: linear-gradient(
        135deg,
        color-mix(in srgb, var(--nova-brand-light) 65%, var(--nova-color-background)) 0%,
        var(--nova-brand-light) 100%
      );
    }

    :host .header-search__aura-card-title {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      line-height: 1.2;
      color: var(--nova-brand-ink);
    }

    :host .header-search__aura-card-price {
      margin: 4px 0 0;
      font-size: 16px;
      line-height: 1.5;
      font-weight: 400;
      color: var(--nova-brand-primary);
    }

    :host .header-search__aura-card-prices {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--nova-space-xs);
      margin-top: 4px;
    }

    :host .header-search__aura-card-prices .header-search__aura-card-price {
      margin: 0;
    }

    :host .header-search__aura-card-compare {
      margin: 0;
      font-size: 14px;
      line-height: 1.4;
      color: var(--nova-color-subdued);
      text-decoration: line-through;
    }

    /* Suggested products (empty-state) still use horizontal strip markup */
    :host .header-search__product-slide-container {
      overflow-x: auto;
      width: 100%;
      max-width: 100%;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    :host .header-search__product-slide-container::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__product-slide-inner-container {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      width: 100%;
      gap: var(--nova-space-xs);
    }

    :host .header-search__product-slide-inner-container > .header-search__product-card {
      display: block;
      width: 41%;
      flex-shrink: 0;
    }

    :host .header-search__product-slide-inner-container > .header-search__product-card:first-of-type {
      padding-left: var(--nova-space-page-margin);
    }

    :host .header-search__product-slide-inner-container > .header-search__product-card:last-of-type {
      padding-right: var(--nova-space-page-margin);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__product-slide-inner-container > .header-search__product-card:first-of-type {
        padding-left: 0;
      }

      :host .header-search__product-slide-inner-container > .header-search__product-card:last-of-type {
        padding-right: 0;
      }
    }

    :host .header-search__list-container {
      display: flex;
      flex-direction: column;
      padding: 0;
      width: 100%;
      gap: var(--nova-space-md);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__result:not(.header-search__result--predictive) {
        padding: 0 var(--nova-space-page-margin) var(--nova-space-sm);
      }
    }

    :host h3 {
      font: var(--nova-h6-font);
      line-height: 150%;
      font-weight: 500;
      margin-top: 0;
    }


    :host a {
      color: inherit;
      text-decoration: none;
    }

    :host ul {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
      padding-left: var(--nova-space-none);
      margin: 0 var(--nova-space-page-margin);
    }

    @media screen and (min-width: 990px) {
      :host ul {
        margin: 0;
      }
    }

    :host li {
      list-style: none;
      color: var(--nova-color-subdued);
    }

    :host a:focus-visible {
      border: 2px solid var(--nova-utility-focus);
    }

    :host .header-search__no-result-title {
      font: var(--nova-h5-font);
      text-transform: Capitalize;
      word-break: break-all;
    }

    :host .header-search__no-result-description {
      color: var(--nova-color-subdued);
    } 

    @media screen and (min-width: 990px) {
      :host .header-search__no-result-description {
        margin: 0;
      }
    }

    @media screen and (max-width: 989px) {
      :host .header-search__mobile-padding {
        padding: 0 var(--nova-space-page-margin) 0;
      }
    }

    :host .header-search__no-result-container {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
    }

    :host .header-search__product-card img {
      width: 100%;
      aspect-ratio: 160 / 214;
      object-fit: cover;
      object-position: center;
    }

    :host h4 {
      font: var(--nova-h6-font);
      margin: var(--nova-space-none);
    }

    :host p {
      margin: var(--nova-space-none);
    }

    :host .header-search__product-compared-price {
      color: var(--nova-color-subdued);
      text-decoration: line-through;
    }

    @media screen and (min-width: 1300px) {
      :host h4 {
        font-size: 18px;
      }
    }
  `,
  ]
}

customElements.define('nova-search-result', NovaSearchResult)
